import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Trans, useTranslation } from 'react-i18next';
import themeManager from '../../../utils/themeManager';

gsap.registerPlugin(ScrollTrigger);

const ExpertiseSection = () => {
  const { t, i18n } = useTranslation('home');
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(themeManager.isDark());

  useEffect(() => {
    const handleThemeChange = (event) => {
      setIsDarkMode(event.detail.theme === 'dark');
    };
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);
  
  const services = useMemo(() => [
    { id: '01', title: t('services.dist.title') || 'Distribucija', description: t('services.dist.description') || 'Vodeći distributer IT opreme u regionu.' },
    { id: '02', title: t('services.xiaomi.title') || 'Xiaomi Store', description: t('services.xiaomi.description') || 'Premium maloprodajno iskustvo.' },
    { id: '03', title: t('services.service.title') || 'Servisni Centar', description: t('services.service.description') || 'Sertifikovan servisni centar.' },
    { id: '04', title: t('services.brands.title') || 'Zastupništva', description: t('services.brands.description') || 'Ekskluzivno zastupništvo globalnih lidera.' },
    { id: '05', title: t('services.b2b.title') || 'B2B Rešenja', description: (
      <Trans
        i18nKey="services.b2b.description"
        components={{
          1: <a href="https://b2b.pccentar.rs/login.php" target="_blank" rel="noopener noreferrer" className="underline decoration-1" />
        }}
        defaults="Access our <1>B2B portal</1> for business users."
      /> ) },
    { id: '06', title: t('services.security.title') || 'Sigurnost', description: t('services.security.description') || 'Sveobuhvatna zaštita podataka.' }
  ], [t, i18n.language]);

  const sectionRef = useRef(null);
  const leftColumnRef = useRef(null);
  const serviceItemsRef = useRef([]);
  const hexagonLinesRef = useRef([]);
  const hexagonContainerRef = useRef(null);

  useEffect(() => {
    let mm = gsap.matchMedia();

    // DESKTOP: Pinning efekat
    mm.add("(min-width: 1024px)", () => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: leftColumnRef.current,
        pinSpacing: false
      });
    });

    const ctx = gsap.context(() => {
      // Animacija punjenja linija hexagona (radi na oba)
      hexagonLinesRef.current.forEach((line, i) => {
        if (!line) return;
        const length = line.getTotalLength();
        gsap.set(line, { strokeDasharray: length, strokeDashoffset: length });

        gsap.to(line, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 20%", 
            end: "bottom 80%",
            scrub: 1,
          }
        });
      });

      // Pojavljivanje stavki usluga
      serviceItemsRef.current.forEach((item) => {
        gsap.from(item, {
          scrollTrigger: {
            trigger: item,
            start: "top 90%",
            toggleActions: "play none none reverse"
          },
          y: 20,
          opacity: 0,
          duration: 0.6
        });
      });
    }, sectionRef);

    return () => {
      ctx.revert();
      mm.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`flex flex-col lg:flex-row relative ${isDarkMode ? 'bg-[#0F1115]' : 'bg-[#F3F4F6]'}`}
    >
      {/* LEVA KOLONA - Sticky sa offsetom za navigaciju */}
      <div
        ref={leftColumnRef}
        className={`
          sticky 
          top-0 lg:top-0 
          z-20 
          w-full lg:w-2/5 
          h-auto lg:h-screen 
          flex items-center justify-center 
          /* Dodajemo gornji padding na mobilnom da pobegnemo od navigacije */
          pt-24 pb-12 lg:pt-0 lg:pb-0 
          px-6 lg:px-12 xl:px-24 
          ${isDarkMode ? 'bg-[#0F1115]/95' : 'bg-[#F3F4F6]/95'} 
          backdrop-blur-md lg:bg-transparent
        `}
      >
        <div className="text-center lg:text-left w-full">
          {/* Mali naslov - smanjen margin-bottom da ne gura glavni naslov previše dole */}
          <div className={`text-[10px] lg:text-sm font-medium tracking-[0.3em] uppercase mb-1 lg:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('expertise.smallTitle') || 'Oblasti delovanja'}
          </div>
          
          <h2 className={`text-4xl md:text-5xl lg:text-7xl xl:text-8xl font-sans font-bold uppercase tracking-tighter leading-tight lg:leading-[0.9] ${isDarkMode ? 'text-white' : 'text-black'}`}>
            {(() => {
              const title = t('expertise.mainTitle') || 'POSLOVNA REŠENJA';
              const words = title.split(' ');
              
              // Određivanje koje reči koristi hexagon po jeziku
              let hexagonWordIndex = i18n.language === 'en' ? 0 : 1; // En: prva reč, Sr: druga reč
              
              // Ako nema dovoljno reči, koristi poslednju
              if (hexagonWordIndex >= words.length) {
                hexagonWordIndex = words.length - 1;
              }
              
              const hexagonWord = words[hexagonWordIndex];
              const hexagonChars = hexagonWord.split('');
              const beforeHexagon = words.slice(0, hexagonWordIndex).join(' ');
              const afterHexagon = words.slice(hexagonWordIndex + 1).join(' ');
              
              return (
                <>
                  {beforeHexagon && (
                    <span className="block lg:inline opacity-90 text-3xl md:text-5xl lg:text-8xl">
                      {beforeHexagon}
                    </span>
                  )}
                  {beforeHexagon && afterHexagon && <br className="hidden lg:block" />}
                  <span className="inline-flex items-center">
                    {hexagonChars.slice(0, -1).join('')}
                    <span className="relative inline-flex items-center">
                      {hexagonChars[hexagonChars.length - 1]}
                      
                      {/* Hexagon kontejner */}
                      <span 
                        ref={hexagonContainerRef}
                        className="relative inline-block ml-3 w-[0.65em] h-[0.75em]"
                      >
                        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 513 595" fill="none">
                          {[...Array(6)].map((_, i) => (
                            <path 
                              key={i}
                              ref={el => hexagonLinesRef.current[i] = el} 
                              stroke="#8B0A1A" 
                              strokeWidth="25" 
                              strokeLinecap="round"
                              d={["M257.5 0.5 L512.5 151.7", "M512.5 151.7 V447.2", "M512.5 447.2 L257.5 593.5", "M257.5 593.5 L0.5 447.2", "M0.5 447.2 V150.3", "M0.5 150.3 L257.5 0.5"][i]}
                            />
                          ))}
                        </svg>
                        <img 
                          src="/assets/images/hero-logo2-removebg-preview.png" 
                          alt="" 
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-auto" 
                        />
                      </span>
                    </span>
                  </span>
                  {afterHexagon && (
                    <span className="block lg:inline opacity-90 text-3xl md:text-5xl lg:text-8xl">
                      {' '}{afterHexagon}
                    </span>
                  )}
                </>
              );
            })()}
          </h2>
        </div>
      </div>

      {/* DESNA KOLONA */}
      <div className="w-full lg:w-3/5 relative z-10">
        <div className="py-8 lg:py-32 px-6 lg:px-12 xl:px-24">
          {services.map((service, index) => (
            <div
              key={service.id}
              ref={el => serviceItemsRef.current[index] = el}
              className={`border-b last:border-b-0 py-12 lg:py-20 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}
              // DODATO: Govorimo botu da je ovo jedna specifična usluga
              itemScope 
              itemType="https://schema.org/Service"
            >
              <div className="flex flex-col sm:flex-row lg:items-start gap-4 lg:gap-16">
                <div className={`text-5xl lg:text-8xl font-sans font-light opacity-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                  {service.id}
                </div>
                <div>
                  {/* DODATO: itemProp="name" povezuje naslov sa uslugom */}
                  <h3 
                    itemProp="name"
                    className={`text-xl lg:text-4xl font-sans font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`}
                  >
                    {service.title}
                  </h3>
                  {/* DODATO: itemProp="description" za opis usluge */}
                  <p 
                    itemProp="description"
                    className={`text-sm lg:text-xl font-sans font-light leading-relaxed max-w-3xl ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div className="pt-12 pb-20 flex justify-center lg:justify-start">
            <a href={location.pathname.startsWith('/en') ? "/en/about" : "/about"} className={`px-10 py-3 border font-sans font-medium transition-all duration-300 ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-gray-900' : 'border-black text-black hover:bg-black hover:text-white'}`}>
              {t('expertise.moreButton') || 'Više o nama'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;